import { useEffect, useState } from "react";
import { useBuilder } from "./context";
import { DEVICES, resolvedStyle, setProp as setOvProp, setStyle } from "./overrides";

const TEXTY = ["text", "heading", "button", "link"];
const BOXY = ["page", "section", "container", "navigation", "footer", "card", "form"];

const labelCls = "mb-1 block text-[9px] font-semibold uppercase tracking-[0.2em] text-neutral-500";
const inputCls = "w-full border border-white/10 bg-white/5 px-2 py-1.5 text-xs text-white placeholder:text-neutral-600 outline-none focus:border-sky-500/60";

function TextField({ label, prop, placeholder, testId }) {
  const b = useBuilder();
  const value = resolvedStyle(b.overrides, b.selected, b.device, prop);
  const inherited = b.device !== "desktop" ? resolvedStyle(b.overrides, b.selected, "desktop", prop) : "";
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value, b.selected, b.device]);
  return (
    <label className="block">
      <span className={labelCls}>{label}</span>
      <input
        value={v}
        placeholder={placeholder || inherited || "—"}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => v !== value && b.commit(setStyle(b.ovRef.current, b.selected, b.device, prop, v))}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        className={inputCls}
        data-testid={testId || `prop-${prop}`}
      />
    </label>
  );
}

function SelectField({ label, prop, options, testId }) {
  const b = useBuilder();
  const value = resolvedStyle(b.overrides, b.selected, b.device, prop);
  return (
    <label className="block">
      <span className={labelCls}>{label}</span>
      <select
        value={value}
        onChange={(e) => b.commit(setStyle(b.ovRef.current, b.selected, b.device, prop, e.target.value))}
        className={`${inputCls} bg-[#1a1a1d]`}
        data-testid={testId || `prop-${prop}`}
      >
        {options.map((o) => <option key={o} value={o}>{o || "Default"}</option>)}
      </select>
    </label>
  );
}

function ColorField({ label, prop }) {
  const b = useBuilder();
  const value = resolvedStyle(b.overrides, b.selected, b.device, prop);
  const isHex = /^#[0-9a-fA-F]{6}$/.test(value);
  return (
    <div>
      <span className={labelCls}>{label}</span>
      <div className="flex gap-1">
        <input
          type="color"
          value={isHex ? value : "#ffffff"}
          onChange={(e) => b.commit(setStyle(b.ovRef.current, b.selected, b.device, prop, e.target.value))}
          className="h-8 w-8 shrink-0 cursor-pointer border border-white/10 bg-transparent"
          data-testid={`prop-${prop}-picker`}
        />
        <TextField label="" prop={prop} placeholder="#ffffff" testId={`prop-${prop}`} />
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-b border-white/10 px-3 py-3">
      <p className="mb-2.5 text-[10px] font-bold uppercase tracking-[0.25em] text-neutral-400">{title}</p>
      <div className="grid grid-cols-2 gap-2">{children}</div>
    </div>
  );
}

export default function PropertiesPanel() {
  const b = useBuilder();
  const root = b.rootRef.current;
  const el = b.selected ? root?.querySelector(`[data-eid="${CSS.escape(b.selected)}"]`) : null;

  if (!el) {
    return (
      <aside className="flex w-72 shrink-0 items-center justify-center border-l border-white/10 bg-[#141416] p-6" data-builder-ui data-testid="properties-panel">
        <p className="text-center text-xs leading-relaxed text-neutral-500">
          Click any element on the canvas to edit it.<br />Double-click text to edit it inline.
        </p>
      </aside>
    );
  }

  const type = el.dataset.etype || "container";
  const cfg = b.overrides.elements?.[b.selected] || {};
  const isTexty = TEXTY.includes(type);
  const isBoxy = BOXY.includes(type);

  return (
    <aside className="w-72 shrink-0 overflow-y-auto border-l border-white/10 bg-[#141416]" data-builder-ui data-testid="properties-panel">
      <div className="border-b border-white/10 px-3 py-2.5">
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white">{type}</p>
        <p className="mt-0.5 truncate text-[10px] text-neutral-500">{b.selected}</p>
        <div className="mt-2 flex border border-white/10" data-testid="responsive-tabs">
          {DEVICES.map((d) => (
            <button
              key={d}
              onClick={() => b.setDevice(d)}
              data-testid={`prop-device-${d}`}
              className={`flex-1 py-1 text-[9px] font-semibold uppercase tracking-wider transition-colors ${b.device === d ? "bg-white text-black" : "text-neutral-400 hover:text-white"}`}
            >
              {d.slice(0, 3)}
            </button>
          ))}
        </div>
        {b.device !== "desktop" && (
          <p className="mt-1.5 text-[9px] text-neutral-500">Editing {b.device} overrides — empty fields inherit from desktop.</p>
        )}
      </div>

      {isTexty && (
        <Section title="Content">
          <div className="col-span-2">
            <span className={labelCls}>Text</span>
            <textarea
              rows={2}
              defaultValue={cfg.text ?? el.innerText}
              key={`${b.selected}-text`}
              onBlur={(e) => b.commit(setOvProp(b.ovRef.current, b.selected, "text", e.target.value))}
              className={`${inputCls} resize-none`}
              data-testid="prop-text-content"
            />
          </div>
          {el.tagName === "A" && (
            <div className="col-span-2">
              <span className={labelCls}>Link URL</span>
              <input
                defaultValue={cfg.href ?? el.getAttribute("href") ?? ""}
                key={`${b.selected}-href`}
                onBlur={(e) => b.commit(setOvProp(b.ovRef.current, b.selected, "href", e.target.value))}
                className={inputCls}
                data-testid="prop-href"
              />
            </div>
          )}
        </Section>
      )}

      {type === "image" && (
        <Section title="Content">
          <div className="col-span-2">
            <span className={labelCls}>Image URL</span>
            <input
              defaultValue={cfg.src ?? el.getAttribute("src") ?? ""}
              key={`${b.selected}-src`}
              onBlur={(e) => b.commit(setOvProp(b.ovRef.current, b.selected, "src", e.target.value))}
              className={inputCls}
              data-testid="prop-img-src"
            />
          </div>
          <div className="col-span-2">
            <span className={labelCls}>Alt text</span>
            <input
              defaultValue={cfg.alt ?? el.getAttribute("alt") ?? ""}
              key={`${b.selected}-alt`}
              onBlur={(e) => b.commit(setOvProp(b.ovRef.current, b.selected, "alt", e.target.value))}
              className={inputCls}
              data-testid="prop-img-alt"
            />
          </div>
          <SelectField label="Fit" prop="objectFit" options={["", "cover", "contain", "fill"]} />
        </Section>
      )}

      <Section title="Layout">
        <SelectField label="Display" prop="display" options={["", "block", "flex", "grid", "inline-block", "inline-flex", "none"]} />
        <SelectField label="Position" prop="position" options={["", "static", "relative", "absolute", "fixed", "sticky"]} />
        <TextField label="Width" prop="width" placeholder="e.g. 100%" />
        <TextField label="Height" prop="height" placeholder="e.g. 400px" />
        <TextField label="Margin T" prop="marginTop" />
        <TextField label="Margin B" prop="marginBottom" />
        <TextField label="Margin L" prop="marginLeft" />
        <TextField label="Margin R" prop="marginRight" />
        <TextField label="Padding T" prop="paddingTop" />
        <TextField label="Padding B" prop="paddingBottom" />
        <TextField label="Padding L" prop="paddingLeft" />
        <TextField label="Padding R" prop="paddingRight" />
        {isBoxy && (
          <>
            <TextField label="Gap" prop="gap" />
            <SelectField label="Direction" prop="flexDirection" options={["", "row", "column", "row-reverse", "column-reverse"]} />
            <SelectField label="Justify" prop="justifyContent" options={["", "flex-start", "center", "flex-end", "space-between", "space-around"]} />
            <SelectField label="Align" prop="alignItems" options={["", "stretch", "flex-start", "center", "flex-end", "baseline"]} />
          </>
        )}
      </Section>

      {isTexty && (
        <Section title="Typography">
          <div className="col-span-2">
            <SelectField label="Font" prop="fontFamily" options={["", "'Cormorant Garamond', serif", "Manrope, sans-serif", "Georgia, serif", "Arial, sans-serif", "monospace"]} />
          </div>
          <TextField label="Size" prop="fontSize" placeholder="e.g. 64px" />
          <SelectField label="Weight" prop="fontWeight" options={["", "300", "400", "500", "600", "700"]} />
          <TextField label="Line height" prop="lineHeight" />
          <TextField label="Spacing" prop="letterSpacing" />
          <SelectField label="Align" prop="textAlign" options={["", "left", "center", "right", "justify"]} />
          <SelectField label="Transform" prop="textTransform" options={["", "none", "uppercase", "lowercase", "capitalize"]} />
          <ColorField label="Color" prop="color" />
        </Section>
      )}

      <Section title="Style">
        <ColorField label="Background" prop="background" />
        <ColorField label="Border color" prop="borderColor" />
        <TextField label="Border width" prop="borderWidth" placeholder="e.g. 1px" />
        <SelectField label="Border style" prop="borderStyle" options={["", "solid", "dashed", "dotted", "none"]} />
        <TextField label="Radius" prop="borderRadius" placeholder="e.g. 8px" />
        <SelectField label="Shadow" prop="boxShadow" options={["", "none", "0 2px 8px rgba(0,0,0,0.4)", "0 10px 30px rgba(0,0,0,0.5)", "0 20px 60px rgba(0,0,0,0.6)", "0 0 40px rgba(255,255,255,0.15)"]} />
        <TextField label="Opacity" prop="opacity" placeholder="0 – 1" />
      </Section>
    </aside>
  );
}
